/**
 * Licences — the shape the command center and the reseller portal both read.
 *
 * `licenceStatus` and `portGateErrors` live here rather than in either app so
 * the chip on the list, the badge on a customer and the builder's refusal
 * cannot disagree about the same licence.
 *
 * WHAT A LICENCE IS: one appliance. Distinct from the customer's AGREEMENT
 * (what they bought) — once a customer has two boxes its purchased ports are
 * split between them, so the two genuinely differ. See
 * docs/superpowers/specs/2026-09-10-licensing-design.md.
 */

import {
  LICENCE_SECTIONS,
  LICENCE_SECTION_LABELS,
  licenceSectionOf,
  type LicenceSection,
  type PriceListCategory,
  type PriceListPricingMode,
  type PriceListUnit,
} from "./priceList.js";

export const LICENCE_PORT_KEYS = [
  "total",
  "tdm",
  "audioConferencing",
  "webConferencing",
  "networkSwitches",
  "audioRecording",
] as const;

export type LicencePortKey = (typeof LICENCE_PORT_KEYS)[number];
export type LicencePorts = Record<LicencePortKey, number>;

export interface LicenceFeature {
  key: string;
  name: string;
}

/** Who created or issued a licence: a reseller company, or XOP staff. */
export type LicenceActorKind = "reseller" | "staff";

export interface LicenceProvenance {
  kind: LicenceActorKind;
  /** Set when `kind` is "reseller". */
  reseller: { id: number; name: string } | null;
}

export interface LicenceIssueSummary {
  serial: string;
  issuedAt: string;
  issuedByLabel: string;
  sha256: string;
  downloadCount: number;
  /** NULL = no cap (the default since 2026-09-11). Every pull is journaled
   * regardless; a cap is something an admin puts on ONE issue on purpose. */
  downloadLimit: number | null;
  downloadsRemaining: number | null;
  /** Who issued it (2026-09-15). ABSENT behind the reseller wall — crm-api
   * omits the key for a `crm_resellers: false` token — and absent from a
   * crm-api that predates the field. */
  issuedBy?: LicenceProvenance;
}

export type LicenceRecordStatus = "draft" | "issued" | "revoked";

/** The CTO-approval workflow. See the 2026-09-11 design.
 *  not_required — a trial, or never needed approval
 *  pending      — sent to the CTO, nothing signed
 *  approved     — approved for exactly the current terms
 *  rejected     — declined; `note` says why */
export type LicenceApprovalStatus = "not_required" | "pending" | "approved" | "rejected";

export interface LicenceApproval {
  status: LicenceApprovalStatus;
  requestedAt: string | null;
  requestedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  note: string | null;
}

/** What pressing Issue WOULD do with the current terms — so the button can say
 * "Issue trial" or "Request approval" before anything is sent. */
/** A licence's TYPE, chosen in the builder (2026-09-15) — never inferred from
 * its dates. A `trial` is allowed only for a customer's FIRST licence and signs
 * at once; a `full` licence always waits for a super admin's approval. */
export type LicenceKind = "trial" | "full";

/** The longest label crm-api stores (a `string` column, and the BFF's zod cap). */
export const LICENCE_LABEL_MAX = 120;

/**
 * What a NEW licence is called before anyone types.
 *
 * The label came back on 2026-09-16 after a day without one: a row in the
 * licences list, a card waiting for approval and the mail to XOP's executives
 * all need to say WHICH licence they are about, and "2 licences awaiting your
 * approval" does not. It is a human name and nothing derives meaning from it —
 * the signed terms do not include it, so an operator may write anything.
 *
 * The suggestion is deliberately SHORT and made of what the builder already
 * knows: the customer it is for, plus the one word that tells two of that
 * customer's licences apart. In the reseller portal the customer IS the
 * reseller's end customer, so the same rule reads correctly on both sides.
 */
export function suggestLicenceLabel(input: {
  customerName: string;
  kind: LicenceKind;
  /** A renewal replaces an expiring licence for the same appliance. */
  renewal?: boolean;
}): string {
  const name = input.customerName.trim().replace(/\s+/g, " ");
  if (!name) return "";
  const suffix = input.renewal ? " · renewal" : input.kind === "trial" ? " · trial" : "";
  return `${name}${suffix}`.slice(0, LICENCE_LABEL_MAX);
}

export interface LicenceTrial {
  eligible: boolean;
  /** May this licence be a trial at all? True only when the customer has no
   * other licence. crm-api decides; the builder only reflects it. */
  available: boolean;
  /** The end of this appliance's one 30-day window. */
  windowEndsOn: string;
  startedOn: string | null;
}

export type LicenceIssueOutcome = "issued" | "approval_requested" | "already_pending";

/** One row of the download journal — every pull of a licence file, refusals
 * included. */
export interface LicenceDownloadRow {
  id: number;
  licenceId: number;
  customerName: string;
  label: string;
  hardwareAddress: string;
  serial: string;
  actorType: string;
  actorLabel: string;
  ip: string | null;
  userAgent: string | null;
  refused: boolean;
  downloadedAt: string;
}

export type LicenceActivityKind =
  | "generated"
  | "approval_requested"
  | "approved"
  | "rejected"
  | "downloaded"
  | "refused"
  /** A REFUSED touch — no token, wrong permission, a probe for a licence
   * outside the caller's scope, a non-admin pressing approve. The attacker's
   * half of "who touched licences". */
  | "denied";

/** One thing that happened to a licence — the Licences ▸ Reports feed.
 * Assembled by crm-api from the rows that already record each act. */
export interface LicenceActivityEvent {
  kind: LicenceActivityKind;
  at: string;
  actor: string;
  licenceId: number;
  customerName: string;
  label: string;
  hardwareAddress: string;
  serial: string | null;
  ip: string | null;
  detail: string | null;
}

/**
 * RENEWAL (2026-09-15). A licence is renewed by drafting its SUCCESSOR: a new
 * `full` licence for the SAME appliance, features and ports, expiring a year
 * after the old one by default. crm-api opens the window `windowDays` before
 * expiry and says so here; the SPA only offers what it will accept.
 *   renewable     — may `POST /licences/:id/renew` succeed right now?
 *   renewableFrom — the day the window opens (null for a perpetual or unissued
 *                   licence, which never renews)
 *   successor     — the renewal already drafted from THIS licence, if any
 *   renews        — the licence THIS one is the renewal OF, if any
 */
export interface LicenceRenewalSuccessor {
  id: number;
  serial: string | null;
  status: LicenceRecordStatus;
  approvalStatus: LicenceApprovalStatus;
  expiresOn: string | null;
  kind: LicenceKind;
}

export interface LicenceRenewalPredecessor {
  id: number;
  serial: string | null;
  expiresOn: string | null;
}

export interface LicenceRenewal {
  renewable: boolean;
  renewableFrom: string | null;
  windowDays: number;
  successor: LicenceRenewalSuccessor | null;
  renews: LicenceRenewalPredecessor | null;
}

/** What a licence from a crm-api that predates renewal reads as: nothing to
 * renew, nothing renewed. */
export const NO_RENEWAL: LicenceRenewal = {
  renewable: false,
  renewableFrom: null,
  windowDays: 30,
  successor: null,
  renews: null,
};

export interface Licence {
  id: number;
  customerId: number;
  customerName: string;
  /** True when the owner is XOP Networks Inc. — one of OUR OWN appliances.
   * Presentation only: it labels the row "Internal" and tells the builder whose
   * box this is. crm-api decides who may WRITE one (the `crm_internal_licences`
   * claim), and never trusts this flag coming back. */
  customerInternal: boolean;
  label: string;
  hardwareAddress: string;
  /** ISO date, or null for a perpetual licence. */
  expiresOn: string | null;
  perpetual: boolean;
  status: LicenceRecordStatus;
  featureKeys: string[];
  features: LicenceFeature[];
  ports: LicencePorts;
  currentIssue: LicenceIssueSummary | null;
  approval: LicenceApproval;
  kind: LicenceKind;
  trial: LicenceTrial;
  /** The renewal state (2026-09-15). The BFF fills `NO_RENEWAL` for a crm-api
   * that predates it, so the SPA never has to null-check the object. */
  renewal: LicenceRenewal;
  /** The customer's reseller. ABSENT (not null) behind the reseller wall —
   * crm-api omits the key for a `crm_resellers: false` token, and the SPA
   * renders it only inside `<ResellerOnly>`. Null = a direct customer. */
  reseller?: { id: number; name: string } | null;
  /** Who created it: a reseller company or XOP staff (2026-09-15). ABSENT
   * behind the reseller wall, for every licence, exactly like `reseller`.
   * Licences created before the field was recorded read as staff. */
  createdBy?: LicenceProvenance;
  notes: string | null;
}

/**
 * One row of the licence CATALOGUE, as the builder's picker sees it.
 *
 * SINCE 2026-09-15 THE CATALOGUE IS CRM ▸ PRICING, not the appliance's key
 * list. A row is a `product_features` row from one of the two licensable
 * categories (Value Added Services / Platform Extensions), and `licenceKey` is
 * the appliance key it is LINKED to — `ProductFeature.licence_key`, curated on
 * the Pricing page. A row with `licenceKey: null` is sold but not yet linked:
 * it is shown greyed and cannot go on a licence, because a licence file names
 * appliance keys and nothing else. The appliance's own 76 keys travel beside
 * the rows as `LicenceCatalog.applianceKeys`, so a licence that already names
 * a key no row links (a legacy one) stays readable and editable.
 *
 * `section` is which of the TWO headings the feature sits under — Value Added
 * Services or Platform Extensions, the appliance's own layout. crm-api stamps
 * it on every row; a reader that meets a row without one derives it with
 * `licenceSectionOf(pricing.category)` (`licenceFeatureSection`).
 */
export interface LicenceCatalogFeature {
  /** The `product_features` row id. */
  id: number;
  /** The 4-digit internal code — always present now (2026-09-15). */
  code: string;
  name: string;
  section: LicenceSection;
  /** The appliance key this feature is linked to, or null when nobody has
   * linked it yet (set in CRM ▸ Pricing). */
  licenceKey: string | null;
  /** Kept for compatibility with readers of the pre-2026-09-15 shape, which
   * carried the appliance's own description. Never populated by the BFF now. */
  description?: string;
  pricing: LicenceCatalogPricing;
}

/** What `GET /api/licences/catalog` returns: the Pricing rows the builder
 * picks from, and every key the appliance understands. */
export interface LicenceCatalog {
  features: LicenceCatalogFeature[];
  applianceKeys: string[];
}

/** One appliance key with the appliance's own description — what CRM ▸
 * Pricing offers when linking a feature (`GET /api/licences/appliance-features`). */
export interface ApplianceFeature {
  key: string;
  description: string;
}

export interface LicenceCatalogPricing {
  /** The product the feature belongs to ("USN"). */
  product: string;
  category: PriceListCategory;
  /** Printed, or the composed `<product code>-<feature code>` fallback. */
  partNumber: string;
  unit: PriceListUnit;
  pricingMode: PriceListPricingMode;
  priceCents: number;
  percentRate: number | null;
  currency: string;
  /** A RETIRED feature stays listed: a licence being renewed may already carry
   * its key, and hiding it would make that licence unreadable in the builder. */
  active: boolean;
}

/** Which of the two sections a catalog feature sits under: the `section`
 * crm-api stamped, else the same rule off its category. Mirrored name for name
 * in xop-resellers' shared package. */
export function licenceFeatureSection(feature: SectionedFeature): LicenceSection {
  return feature.section ?? licenceSectionOf(feature.pricing?.category);
}

/** The least a row needs for `licenceFeatureSection` to place it: a stamped
 * section, or a category to derive one from. */
export interface SectionedFeature {
  section?: LicenceSection | undefined;
  pricing?: { category: PriceListCategory } | undefined;
}

export interface LicenceCatalogSection<F extends SectionedFeature> {
  section: LicenceSection;
  label: string;
  features: F[];
}

/**
 * A catalog folded into its TWO sections, in the appliance's fixed order —
 * Value Added Services, then Platform Extensions — with an empty section
 * omitted. There is never a third entry: an unsold key is a Platform Extension.
 */
export function licenceCatalogSections<F extends SectionedFeature>(
  features: readonly F[],
): Array<LicenceCatalogSection<F>> {
  const bySection = new Map<LicenceSection, F[]>();
  for (const feature of features) {
    const section = licenceFeatureSection(feature);
    const bucket = bySection.get(section);
    if (bucket) bucket.push(feature);
    else bySection.set(section, [feature]);
  }
  return LICENCE_SECTIONS.flatMap((section) => {
    const rows = bySection.get(section);
    return rows && rows.length > 0
      ? [{ section, label: LICENCE_SECTION_LABELS[section], features: rows }]
      : [];
  });
}

export type LicenceStatus = "expired" | "expiring" | "active" | "perpetual";

export const LICENCE_EXPIRY_WARNING_DAYS = 60;

/**
 * A licence with no end date is PERPETUAL, not "expiring furthest away". The
 * absence is the meaning — encoding it as a far-future date would make every
 * surface lie about it eventually.
 */
export function licenceStatus(licence: Pick<Licence, "expiresOn">): LicenceStatus {
  if (!licence.expiresOn) return "perpetual";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(`${licence.expiresOn}T00:00:00`);
  const days = Math.round((end.getTime() - today.getTime()) / 86_400_000);

  if (days < 0) return "expired";
  if (days <= LICENCE_EXPIRY_WARNING_DAYS) return "expiring";
  return "active";
}

/**
 * The appliance force-disables these when their DERIVED port count is zero
 * (feature.rb:492-495, then :521-541) and reports it nowhere — not in the
 * upload response, not in its API, not in a log the customer can see.
 *
 * `webcast` is in that loop upstream but is not a licensable key, so its branch
 * is dead and it is deliberately absent here. `web_conferencing` is NOT gated:
 * the flag survives with zero capacity, which the builder warns about rather
 * than refusing.
 */
export const PORT_GATED_FEATURES: ReadonlyArray<{ feature: string; port: LicencePortKey }> = [
  { feature: "conferencing", port: "audioConferencing" },
  { feature: "mass_notification", port: "audioConferencing" },
];

export function portGateErrors(licence: Pick<Licence, "featureKeys" | "ports">): string[] {
  const selected = new Set(licence.featureKeys);

  return PORT_GATED_FEATURES.filter(
    ({ feature, port }) => selected.has(feature) && licence.ports[port] <= 0,
  ).map(
    ({ feature, port }) =>
      `${feature} is licensed but ${port} is 0 — the appliance will switch it off without saying so.`,
  );
}

/** Legal but useless: the flag stays on with no capacity behind it. */
export function portGateWarnings(licence: Pick<Licence, "featureKeys" | "ports">): string[] {
  if (!licence.featureKeys.includes("web_conferencing")) return [];
  if (licence.ports.webConferencing > 0) return [];

  return ["web_conferencing is licensed with 0 web ports — the feature stays on but has no capacity."];
}

/**
 * `Feature.get_system_id` returns MD5 over the box's network config as
 * hex8-hex8-hex8-hex8 (lowercase) — unless libvalfilecheck.so is present, in
 * which case it returns an uppercase MAC. The appliance compares whichever it
 * reports as an exact string, BEFORE it checks the signature, so a typo fails at
 * the customer's console.
 *
 * So an operator may type a MAC in ANY standard notation (2026-09-15) — IEEE 802
 * hyphens, colons, Cisco dots or bare hex, any case — and the system id in any
 * case, and the address is NORMALISED to the one form the appliance reports.
 * Loosening what is STORED would ship files that fail at the console; this
 * loosens only what is TYPED. crm-api applies the identical rule
 * (`Licence.normalize_hardware_address`) and the reseller portal mirrors it.
 */
const SYSTEM_ID = /^[0-9a-f]{8}-[0-9a-f]{8}-[0-9a-f]{8}-[0-9a-f]{8}$/i;
const MAC_NOTATIONS = [
  /^[0-9a-f]{2}(?::[0-9a-f]{2}){5}$/i, // AA:BB:CC:DD:EE:FF
  /^[0-9a-f]{2}(?:-[0-9a-f]{2}){5}$/i, // AA-BB-CC-DD-EE-FF (IEEE 802)
  /^[0-9a-f]{4}(?:\.[0-9a-f]{4}){2}$/i, // aabb.ccdd.eeff (Cisco)
  /^[0-9a-f]{12}$/i, // aabbccddeeff
];

export const HARDWARE_ADDRESS_UNREADABLE =
  "Not a hardware address the appliance reports. Use its system id (xxxxxxxx-xxxxxxxx-xxxxxxxx-xxxxxxxx) or its MAC address (e.g. AA:BB:CC:DD:EE:FF).";

type AddressVerdict = { canonical: string; problem: null } | { canonical: null; problem: string };

function readHardwareAddress(input: string): AddressVerdict {
  const compact = input.replace(/\s+/g, "");
  if (SYSTEM_ID.test(compact)) return { canonical: compact.toLowerCase(), problem: null };
  if (!MAC_NOTATIONS.some((notation) => notation.test(compact))) {
    return { canonical: null, problem: HARDWARE_ADDRESS_UNREADABLE };
  }
  const hex = compact.replace(/[:.\-]/g, "").toUpperCase();
  if (/^0{12}$/.test(hex)) {
    return { canonical: null, problem: "00:00:00:00:00:00 is not an address any network card reports." };
  }
  if (/^F{12}$/.test(hex)) {
    return { canonical: null, problem: "That is the broadcast address, not a network card's own address." };
  }
  // The least-significant bit of the first octet marks a GROUP address.
  if (Number.parseInt(hex.slice(0, 2), 16) & 1) {
    return { canonical: null, problem: "That is a multicast address, not a network card's own address." };
  }
  return { canonical: hex.match(/.{2}/g)!.join(":"), problem: null };
}

/** The address in the exact form the appliance reports, or null when it is
 * not one. */
export function normalizeHardwareAddress(input: string): string | null {
  return readHardwareAddress(input).canonical;
}

/** Why an address will not do, in a sentence — or null when it will, or when
 * nothing has been typed yet (an untouched field is not an error). */
export function hardwareAddressProblem(input: string): string | null {
  if (!input.trim()) return null;
  return readHardwareAddress(input).problem;
}

export function isValidHardwareAddress(address: string): boolean {
  return normalizeHardwareAddress(address) !== null;
}

/**
 * Would these terms issue as a TRIAL? Mirrors crm-api's `Licence#trial?`: an
 * end date inside the appliance's one 30-day window (`trial.windowEndsOn`).
 * Perpetual never is. ISO dates compare lexicographically, so no Date parsing
 * and no timezone — the builder uses this to label the Issue button before
 * anything is sent.
 */
export function isTrialTerm(expiresOn: string | null | undefined, windowEndsOn: string): boolean {
  return Boolean(expiresOn) && (expiresOn as string) <= windowEndsOn;
}
